import React, { useState, useRef } from "react";
import Layout from "../components/Layout";
import "../assets/css/AddCase.css";
import axios from "axios";
import { useAuth } from "../context/AuthProvider";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const AddCase = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [caseData, setCaseData] = useState({
    year: "",
    caseType: "",
    caseNumber: "",
    grouping: "",
    petitionerName: "",
    petitionerAdvocate: "",
    petitionerAddress: "",
    respondentName: "",
    respondentAdvocate: "",
    respondentAddress: "",
    status: "", // Added status field
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Validate form data
  const validateForm = () => {
    const errors = [];
    const currentYear = new Date().getFullYear();

    // Year validation
    if (!caseData.year || caseData.year < 1900 || caseData.year > currentYear) {
      errors.push(`Year must be between 1900 and ${currentYear}`);
    }

    // Case number format validation
    if (!caseData.caseNumber.match(/^[A-Za-z0-9-]+$/)) {
      errors.push('Case number can only contain letters, numbers, and hyphens');
    }

    // Required fields validation
    const requiredFields = [
      'caseType',
      'petitionerName',
      'petitionerAdvocate',
      'respondentName',
      'respondentAdvocate',
      'status' // Add status to required fields
    ];

    requiredFields.forEach(field => {
      if (!caseData[field]?.trim()) {
        errors.push(`${field.replace(/([A-Z])/g, ' $1').trim()} is required`);
      }
    });

    return errors;
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCaseData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    
    // Check file size (5MB limit)
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    const oversizedFiles = newFiles.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      toast.error(`Some files exceed 5MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }

    // Check file types
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
    const invalidFiles = newFiles.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      toast.error(`Invalid file type(s): ${invalidFiles.map(f => f.name).join(', ')}. Only PDF and images are allowed.`);
      return;
    }

    // Check total file count
    if (selectedFiles.length + newFiles.length > 10) {
      toast.error('Maximum 10 files can be uploaded');
      return;
    }

    setSelectedFiles((prevFiles) => {
      const existingFileNames = prevFiles.map((file) => file.name);
      const uniqueNewFiles = newFiles.filter(
        (file) => !existingFileNames.includes(file.name)
      );
      return [...prevFiles, ...uniqueNewFiles];
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  // Handle browse button click
  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  // Handle file removal
  const handleRemoveFile = (fileNameToRemove) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((file) => file.name !== fileNameToRemove)
    );
  };

  // Reset form
  const resetForm = () => {
    setCaseData({
      year: "",
      caseType: "",
      caseNumber: "",
      grouping: "",
      petitionerName: "",
      petitionerAdvocate: "",
      petitionerAddress: "",
      respondentName: "",
      respondentAdvocate: "",
      respondentAddress: "",
      status: "", // Reset status
    });
    setSelectedFiles([]);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setUploadProgress(0);

    // Validate form
    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      setIsSubmitting(false);
      return;
    }

    // Check authentication
    if (!user?.token) {
      toast.error("Authentication token not found. Please log in again.");
      setIsSubmitting(false);
      return;
    }

    // Check files
    if (selectedFiles.length === 0) {
      toast.error("Please select at least one file to upload.");
      setIsSubmitting(false);
      return;
    }

    // Prepare form data
    const formData = new FormData();
    Object.keys(caseData).forEach(key => {
      if (caseData[key]) {
        formData.append(key, caseData[key]);
      }
    });

    selectedFiles.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await axios.post(`${BASE_URL}/api/cases`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        timeout: 30000,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      if (response.data.status === "success") {
        toast.success("Case added successfully!");
        resetForm();
        navigate("/cases");
      } else {
        toast.error(response.data.message || "Failed to add case.");
      }
    } catch (error) {
      if (error.response) {
        // Server responded with error
        const errorMessage = error.response.data.message || "Server error occurred";
        toast.error(errorMessage);
      } else if (error.request) {
        // Request made but no response
        toast.error("No response from server. Please check your connection.");
      } else {
        // Request setup error
        toast.error("Error setting up request. Please try again.");
      }
      console.error("Error details:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="add-case-container">
        <div className="form-wrapper">
          <h1 className="form-title">Add New Case</h1>
          <p className="form-subtitle">
            Fill in the details below to register a new case.
          </p>

          <form onSubmit={handleSubmit}>
            {/* Case Details Section */}
            <div className="form-section">
              <h2 className="section-title">Case Details</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="year">Year</label>
                  <input
                    type="number"
                    id="year"
                    name="year"
                    placeholder="e.g., 2024"
                    value={caseData.year}
                    onChange={handleInputChange}
                    required
                    min="1900"
                    max={new Date().getFullYear()}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="caseType">Case Type</label>
                  <input
                    type="text"
                    id="caseType"
                    name="caseType"
                    placeholder="e.g., Civil, Criminal"
                    value={caseData.caseType}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="caseNumber">Case Number</label>
                  <input
                    type="text"
                    id="caseNumber"
                    name="caseNumber"
                    placeholder="e.g., CIV-2024-001"
                    value={caseData.caseNumber}
                    onChange={handleInputChange}
                    required
                    pattern="[A-Za-z0-9-]+"
                    title="Only letters, numbers, and hyphens are allowed"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="grouping">Grouping</label>
                  <input
                    type="text"
                    id="grouping"
                    name="grouping"
                    placeholder="e.g., Group A"
                    value={caseData.grouping}
                    onChange={handleInputChange}
                  />
                </div>
                {/* date fild */}
                <div className="form-group">
                  <label htmlFor="date">Date</label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={caseData.date}
                    onChange={handleInputChange}
                  />
                </div>
                 {/* Status Field */}
                 <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <input
                    type="text"
                    id="status"
                    name="status"
                    placeholder="Enter status (e.g., Open, Closed, Pending)"
                    value={caseData.status}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Petitioner and Respondent Details */}
            <div className="form-grid">
              <div className="form-section">
                <h2 className="section-title">Petitioner's Details</h2>
                <div className="form-group">
                  <label htmlFor="petitionerName">Petitioner Name</label>
                  <input
                    type="text"
                    id="petitionerName"
                    name="petitionerName"
                    placeholder="Enter petitioner's name"
                    value={caseData.petitionerName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="petitionerAdvocate">Advocate's Name</label>
                  <input
                    type="text"
                    id="petitionerAdvocate"
                    name="petitionerAdvocate"
                    placeholder="Enter advocate's name"
                    value={caseData.petitionerAdvocate}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="petitionerAddress">Address</label>
                  <textarea
                    id="petitionerAddress"
                    name="petitionerAddress"
                    placeholder="Enter petitioner's address"
                    rows="3"
                    value={caseData.petitionerAddress}
                    onChange={handleInputChange}
                  ></textarea>
                </div>
              </div>

              <div className="form-section">
                <h2 className="section-title">Respondent's Details</h2>
                <div className="form-group">
                  <label htmlFor="respondentName">Respondent Name</label>
                  <input
                    type="text"
                    id="respondentName"
                    name="respondentName"
                    placeholder="Enter respondent's name"
                    value={caseData.respondentName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="respondentAdvocate">Advocate's Name</label>
                  <input
                    type="text"
                    id="respondentAdvocate"
                    name="respondentAdvocate"
                    placeholder="Enter advocate's name"
                    value={caseData.respondentAdvocate}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="respondentAddress">Address</label>
                  <textarea
                    id="respondentAddress"
                    name="respondentAddress"
                    placeholder="Enter respondent's address"
                    rows="3"
                    value={caseData.respondentAddress}
                    onChange={handleInputChange}
                  ></textarea>
                </div>
              </div>
            </div>

            {/* File Upload Section */}
            <div className="form-section full-width-section">
              <h2 className="section-title">Case Files</h2>
              <div className="form-group">
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept=".pdf,.jpg,.jpeg,.png,.gif"
                />
                <div className="file-upload-container">
                  <button
                    type="button"
                    onClick={handleBrowseClick}
                    className="browse-btn"
                    disabled={selectedFiles.length >= 10}
                  >
                    {selectedFiles.length >= 10 ? 'Maximum files reached' : 'Browse Files'}
                  </button>
                  <p className="file-upload-hint">
                    Maximum 10 files, 5MB each. Allowed types: PDF, JPG, PNG, GIF
                  </p>
                  {selectedFiles.length > 0 && (
                    <div className="file-tags-container">
                      {selectedFiles.map((file) => (
                        <div key={file.name} className="file-tag">
                          <span className="file-name">{file.name}</span>
                          <span className="file-size">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                          <button
                            type="button"
                            className="remove-tag-btn"
                            onClick={() => handleRemoveFile(file.name)}
                            title="Remove file"
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Upload Progress */}
            {isSubmitting && uploadProgress > 0 && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{uploadProgress}% Uploaded</span>
              </div>
            )}

            {/* Submit Button */}
            <button 
              type="submit" 
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding Case...' : 'Add Case'}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AddCase;